// 칭찬하고 할인받기(개선 5~12) 공통 로직.
// 할인 후보 = [min, min+unit, ..., <=max] (단위 격자). 손님이 칭찬을 고르면 이 중 1개를 가중 랜덤으로 정한다.
//  - 금액이 클수록 확률이 낮다(선형 감소 가중치). 최대 금액은 가장 드물게(대략 10% 내외) 나온다.
//  - 할인액 생성은 반드시 서버에서 수행한다(클라이언트 신뢰 금지). 본 함수는 서버/테스트에서 호출.

import { z } from "zod";

// GET /me/compliment 응답. 손님 주문 화면의 "칭찬하고 할인받기" 칸 상태.
export interface ComplimentStateDto {
  // 기능 사용 여부(운영설정 compliment_enabled).
  enabled: boolean;
  // 이미 칭찬을 골라 잠긴 상태인지(주문 종결 전까지 고정).
  locked: boolean;
  // 잠긴 선택이 이미 진행 중인 주문에 적용돼 있는지. true 면 새 주문에는 할인이 다시 적용되지 않는다(중복 방지).
  appliedToOrder: boolean;
  // 잠긴 경우 고른 칭찬 문구.
  complimentText: string | null;
  // 잠긴 경우 적용 할인액(원).
  discountKrw: number;
  // 잠기지 않은 경우 무작위로 보여줄 후보 칭찬들(주문 유형별 개수).
  options: string[];
}

// POST /me/compliment/select 입력. 손님이 고른 칭찬 문구.
export const complimentSelectInputSchema = z.object({
  complimentText: z.string().trim().min(1).max(50),
});
export type ComplimentSelectInput = z.infer<typeof complimentSelectInputSchema>;

// 칭찬 할인 후보 금액 목록(오름차순). 범위가 비정상이면 빈 배열.
export function complimentDiscountValues(
  minKrw: number,
  maxKrw: number,
  unitKrw: number,
): number[] {
  const unit = unitKrw > 0 ? unitKrw : 1;
  if (maxKrw < minKrw) {
    return [];
  }
  const values: number[] = [];
  // min 을 단위 격자에 맞춰 위로 올린 지점부터 시작.
  const start = Math.ceil(minKrw / unit) * unit;
  for (let v = start; v <= maxKrw; v += unit) {
    values.push(v);
  }
  // 격자에 맞는 값이 없지만 min===max 인 특수 케이스는 그 값을 그대로 후보로 둔다.
  if (values.length === 0 && minKrw === maxKrw) {
    return [minKrw];
  }
  return values;
}

// 가중 랜덤 할인액 1개. index 0(최소금액) 가중치가 가장 크고 선형 감소(n-i)라 큰 금액일수록 드물다.
//  - rng 는 [0,1) 난수 공급자(테스트에서 주입 가능). 기본 Math.random.
export function pickWeightedComplimentDiscount(
  minKrw: number,
  maxKrw: number,
  unitKrw: number,
  rng: () => number = Math.random,
): number {
  const values = complimentDiscountValues(minKrw, maxKrw, unitKrw);
  if (values.length === 0) {
    return 0;
  }
  if (values.length === 1) {
    return values[0]!;
  }
  const n = values.length;
  // 선형 감소 가중치: [n, n-1, ..., 1]. 합 = n(n+1)/2.
  const total = (n * (n + 1)) / 2;
  let r = rng() * total;
  for (let i = 0; i < n; i += 1) {
    r -= n - i;
    if (r < 0) {
      return values[i]!;
    }
  }
  return values[n - 1]!;
}
